/**
 * 動画内クリック領域（ホットスポット）の幾何計算（ADR-0022）。
 * 埋め込みプレイヤーと管理画面のエディタで同じ計算を使い、見た目と当たり判定をずらさない。
 */

export type Box = { left: number; top: number; width: number; height: number }

/** 映像の縦横比が分からないときの既定（ワイド型は 16:9 の横型動画専用・ADR-0016） */
const FALLBACK_ASPECT = 16 / 9

/**
 * `object-fit: contain` で表示した映像が、コンテナの中で実際に描かれている矩形を求める。
 * 黒帯（レターボックス）の分だけ内側に入る。座標はコンテナ左上からの px。
 */
export function computeContentBox(
  containerWidth: number,
  containerHeight: number,
  videoWidth: number,
  videoHeight: number,
): Box {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 }
  }
  const aspect = videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : FALLBACK_ASPECT
  const width = Math.min(containerWidth, containerHeight * aspect)
  const height = width / aspect
  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
  }
}

/** 押しやすさの下限（px）。スマホで指が当たる大きさ（WCAG 2.5.5 の目安） */
export const MIN_HIT_SIZE = 44

/**
 * 0〜1 の矩形を、映像矩形の中の px に直す。実寸が `minSize` を切るときは、中心を保ったまま
 * 当たり判定だけを広げる（映像の外へははみ出させない）。
 */
export function toHitBox(
  rect: { x: number; y: number; w: number; h: number },
  content: { width: number; height: number },
  minSize = MIN_HIT_SIZE,
): Box {
  const expand = (start: number, size: number, limit: number) => {
    const target = Math.min(Math.max(size, minSize), limit)
    const grown = start - (target - size) / 2
    return { start: Math.min(Math.max(grown, 0), limit - target), size: target }
  }
  const horizontal = expand(rect.x * content.width, rect.w * content.width, content.width)
  const vertical = expand(rect.y * content.height, rect.h * content.height, content.height)
  return { left: horizontal.start, top: vertical.start, width: horizontal.size, height: vertical.size }
}

/** 0〜1 の矩形を、映像矩形の中の px にそのまま直す（見えるボタンの絵の位置・ADR-0024） */
export function toRectBox(
  rect: { x: number; y: number; w: number; h: number },
  content: { width: number; height: number },
): Box {
  return {
    left: rect.x * content.width,
    top: rect.y * content.height,
    width: rect.w * content.width,
    height: rect.h * content.height,
  }
}

/** その時刻に出ているクリック領域か（開始・終了を含む） */
export function isHotspotActiveAt(hotspot: { startTime: number; endTime: number }, time: number): boolean {
  return time >= hotspot.startTime && time <= hotspot.endTime
}

/** 末尾まで表示する領域。丸めによる0.15秒以内の差を許容する（同じ動画内への移動の保持判定用）。 */
export function isHotspotAtVideoEnd(hotspot: { startTime: number; endTime: number }, duration: number): boolean {
  return Number.isFinite(duration) && duration > 0 && hotspot.startTime < duration && hotspot.endTime >= duration - 0.15
}

/** 別動画へのボタンは動画末尾の0.15秒前で閉じ、終了画面には持ち越さない（終了後も残すもの＝isTransitionKeptAfterEnd は除く）。 */
export function isVideoTransitionClosing(time: number, duration: number): boolean {
  return Number.isFinite(duration) && duration > 0 && time >= Math.max(0, duration - 0.15)
}

/**
 * 「別の動画へ」のボタンを終了後も残すには、終了の何秒前までに出し始めておくか。
 * 分岐の選択肢と同じく「終了3秒前から出して、終了後も残す」（ADR-0023 A-1・LOOV 特許対策）。
 */
export const TRANSITION_KEEP_LEAD_SECONDS = 3

/**
 * 終了後もその場に残す「別の動画へ」のボタン。終了3秒前より前から出ていて、動画の最後まで出すものだけ。
 * 再生中から出ているボタンが最後の場面に残るだけなので、「動画が終わってから初めて出す」形にならない。
 * 残り3秒を切ってから出し始めるものは、これまでどおり末尾で閉じる。
 */
export function isTransitionKeptAfterEnd(hotspot: { startTime: number; endTime: number }, duration: number): boolean {
  return isHotspotAtVideoEnd(hotspot, duration) && hotspot.startTime <= duration - TRANSITION_KEEP_LEAD_SECONDS + 0.05
}

/** 動画が終わってもその場に残す領域か（同じ動画内への移動・終了後も残す「別の動画へ」） */
export function isKeptAtVideoEnd(
  hotspot: { startTime: number; endTime: number },
  kind: 'seek' | 'video_transition' | 'external_link',
  duration: number,
): boolean {
  if (kind === 'seek') return isHotspotAtVideoEnd(hotspot, duration)
  if (kind === 'video_transition') return isTransitionKeptAfterEnd(hotspot, duration)
  return false
}
